import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/supabaseClient';
import { useMultiStepForm } from './useMultiStepForm';
import { StepIndicator } from './StepIndicator';
import { FormNavigation } from './FormNavigation';
import { ProjectBasicsStep } from './steps/ProjectBasicsStep';
import { FeaturesStep } from './steps/FeaturesStep';
import { TimelineStep } from './steps/TimelineStep';
import { ProposalPreviewStep } from './steps/ProposalPreviewStep';
import { formSchema, defaultValues, FormValues } from './FormSchema';

interface LabsMultiStepFormProps {
  onSuccess: () => void;
}

/**
 * Main multi-step form container component
 */
export function LabsMultiStepForm({ onSuccess }: LabsMultiStepFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Configure react-hook-form with zod validation
  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange'
  });
  
  // Set up form steps
  const formSteps = [
    { id: 1, name: 'Project Basics' },
    { id: 2, name: 'Features' },
    { id: 3, name: 'Timeline' },
    { id: 4, name: 'Review' }
  ];
  
  // Use our custom hook for multi-step form navigation
  const { 
    currentStep, 
    goToNextStep, 
    goToPreviousStep, 
    goToStep,
    isFirstStep, 
    isLastStep 
  } = useMultiStepForm({ 
    steps: formSteps.length 
  });
  
  // Handle form submission
  const onSubmit = async (data: FormValues) => {
    console.log('Form submission attempted, isLastStep:', isLastStep, 'showConfirmation:', showConfirmation);
    
    // First check if this is a final confirmation submission
    if (isLastStep && !showConfirmation) {
      console.log('Showing confirmation dialog');
      // If on review page but not yet confirmed, show confirmation dialog
      setShowConfirmation(true);
      return;
    }
    
    // If we're on the last step, confirmed, and the submit button is clicked,
    // then actually submit the form to Supabase
    if (isLastStep && showConfirmation) {
      try {
        setIsSubmitting(true);
        setSubmitError(null);
        
        // Bot protection checks
        // 1. Check if honeypot field is filled (bots often fill all fields)
        if (data.honeypot) {
          console.log("Bot detected through honeypot field");
          // Simulate success but don't actually submit
          setTimeout(() => {
            methods.reset(defaultValues);
            onSuccess();
            setIsSubmitting(false);
          }, 1000);
          return;
        }
        
        // 2. Time-based validation (form filled too quickly)
        const formStartTime = data.form_start_time || 0;
        const currentTime = Date.now();
        const timeTakenSeconds = (currentTime - formStartTime) / 1000;
        
        // If form was completed in under 10 seconds, likely a bot
        if (timeTakenSeconds < 10) {
          console.log("Bot detected through time-based validation");
          // Simulate success but don't actually submit
          setTimeout(() => {
            methods.reset(defaultValues);
            onSuccess();
            setIsSubmitting(false);
          }, 1000);
          return;
        }
        
        // Proceed with normal submission for real users
        const { error } = await supabase.from("client_requests").insert([{
          full_name: data.full_name,
          email: data.email,
          company_name: data.company_name,
          project_type: data.project_type,
          project_description: data.project_description,
          features: data.features,
          has_design: data.has_design,
          timeline: data.timeline,
          budget_range: data.budget_range,
          start_date: data.start_date
        }]);
        
        if (error) throw error;
        
        methods.reset(defaultValues);
        onSuccess();
      } catch (err: any) {
        setSubmitError(err.message || "Something went wrong while submitting your request.");
        console.error("Error submitting form:", err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // If we're not on the last step, just go to the next step
      goToNextStep();
    }
  };
  
  // Render the current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <ProjectBasicsStep />;
      case 2:
        return <FeaturesStep />;
      case 3:
        return <TimelineStep />;
      case 4:
        return <ProposalPreviewStep />;
      default:
        return <ProjectBasicsStep />;
    }
  };
  
  // Handle step-specific validation when clicking Next
  const handleNext = async () => {
    let isValid = false;
    console.log('handleNext called on step:', currentStep);
    
    // Validate fields based on current step
    if (currentStep === 1) {
      console.log('Validating step 1 fields');
      isValid = await methods.trigger(['full_name', 'email', 'project_type']);
    } else if (currentStep === 2) {
      console.log('Validating step 2 fields');
      isValid = await methods.trigger(['project_description']);
    } else if (currentStep === 3) {
      console.log('Validating step 3 fields (timeline, budget & start date)');
      // Make sure we get the current values to check
      const timeline = methods.getValues('timeline');
      const budgetRange = methods.getValues('budget_range');
      const startDate = methods.getValues('start_date');
      console.log('Current values - timeline:', timeline, 'budget:', budgetRange, 'start date:', startDate);
      
      // Explicitly validate all fields
      isValid = await methods.trigger(['timeline', 'budget_range', 'start_date']);
      
      // Double check that all required fields are set
      if (!timeline || !budgetRange || !startDate) {
        console.log('Timeline, budget or start date not set');
        if (!timeline) {
          methods.setError('timeline', { 
            type: 'required', 
            message: 'Please select a timeline' 
          });
        }
        if (!budgetRange) {
          methods.setError('budget_range', { 
            type: 'required', 
            message: 'Please select a budget range' 
          });
        }
        if (!startDate) {
          methods.setError('start_date', { 
            type: 'required', 
            message: 'Please select a start date' 
          });
        }
        isValid = false;
      }
    } else if (currentStep === 4) {
      console.log('On review page, setting isValid to true');
      isValid = true;
    }
    
    console.log('Validation result:', isValid);
    if (isValid) {
      if (currentStep === 4) {
        console.log('Attempting to submit from handleNext');
        methods.handleSubmit(onSubmit)();
      } else {
        goToNextStep();
      }
    }
  };

  // Reset confirmation state if we navigate away from the last step
  React.useEffect(() => {
    if (currentStep !== formSteps.length && showConfirmation) {
      setShowConfirmation(false);
    }
  }, [currentStep, formSteps.length, showConfirmation]);
  
  return (
    <div className="bg-[#080815]/80 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-purple-500/5 to-transparent opacity-70"></div>
      
      <div className="relative z-10">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
            {/* Progress indicator */}
            <StepIndicator 
              currentStep={currentStep} 
              steps={formSteps}
              onStepClick={(step) => {
                // Only allow navigation to previous steps
                if (step < currentStep) {
                  goToStep(step);
                }
              }}
            />
            
            {/* Step title */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">
                {formSteps.find(step => step.id === currentStep)?.name}
              </h2>
              <div className="h-1 w-20 bg-gradient-to-r from-primary to-purple-500 mt-2 rounded-full"></div>
            </div>
            
            {/* Confirmation message */}
            {isLastStep && showConfirmation && (
              <div className="bg-gradient-to-r from-fuchsia-500/20 to-purple-500/20 border border-fuchsia-500/40 text-white p-4 rounded-md mb-6 animate-pulse">
                <h3 className="font-bold mb-1">Ready to Submit?</h3>
                <p className="text-white/80">Please review your information above and click "Confirm & Submit" when you're ready.</p>
              </div>
            )}

            {/* Error message */}
            {submitError && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-md mb-4">
                {submitError}
              </div>
            )}
            
            {/* Current step content */}
            {renderStepContent()}
            
            {/* Navigation buttons */}
            <FormNavigation 
              onNext={handleNext}
              onPrevious={goToPreviousStep}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              isSubmitting={isSubmitting}
              showConfirmation={showConfirmation}
              onCancelConfirmation={() => {
                console.log('Cancelling confirmation');
                setShowConfirmation(false);
              }}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
